import React, {useEffect, useState} from 'react';
import { Card, Col, ConfigProvider, Flex, Layout, message, Popconfirm, Row, theme, Tooltip } from 'antd'
import Summary from "../../components/brief/planner/Summary";
import DataTotals from "../../components/brief/planner/dataTotals";
import { Link, useParams } from 'react-router-dom'
import useSWR from "swr";
import urls from "../../api/urls";
import {api, HydraData} from "../../api/apiProvider";
import {DataTotal, Period} from "../../api/types";
import IconBackArrow from "../../components/ui/icons/IconBackArrow";
import {t} from "i18next";
import ButtonBudgeting from "../../components/ui/buttons/ButtonBudgeting";
import ButtonBlack from "../../components/ui/buttons/ButtonBlack";
import dayjs from "dayjs";
import {subscribeTo} from "../../mercure";
import Roles from "../../roles/Roles";

import {CalculationStatus} from "../../api/constants";
import useDataTotals, {DataTotalsFilterInterface} from "../../components/brief/planner/dataTotals/useDataTotals";
import CompareItems from "../../components/brief/planner/CompareItems";

const {Header, Content, Sider} = Layout;

const dateFormat = 'DD.MM.YYYY';

const acceptPlan = async (id: number) => {
  return await api.patch(`${urls.dataTotals}/${id}/accept`, {
    arg: {
      isAccepted: true,
    }
  })
};

const cancelPlanAccept = async (id: number) => {
  return await api.patch(`${urls.dataTotals}/${id}/accept`, {
    arg: {
      isAccepted: false,
    }
  })
};

export default function Planner() {
  const routeParams = useParams();
  const periodId = Number(routeParams.period) || 0;
  const briefId = Number(routeParams.id) || 0;

  const [selectedPlanId, setSelectedPlanId] = useState(0);
  const [selectedForComparesIds, setSelectedForComparesIds] = useState<string[]>([]);
  const [showCompare, setShowCompare] = useState<boolean>(false);
  const [showFactTotal, setShowFactTotal] = useState<boolean>(false);
  const [isTransferingToBudget, setIsTransferingToBudget] = useState<boolean>(false);
  const [historicData, setHistoricData] = useState<DataTotal>();
  const [isSubscribedToDataTotals, setIsSubscribedToDataTotals] = useState<boolean>(false);
  const [dataTotalsFilter, setDataTotalsFilter] = useState<DataTotalsFilterInterface>({
    channels: [],
    factors: [],
  });

  const [showHistoricData, setShowHistoricData] = useState<boolean>(true);

  const {
    dataTotals,
    isDataTotalsLoading,
    mutateDataTotal,
    isFactData,
    mutateDataTotals,
    isDataTotalLoading,
  } = useDataTotals(periodId, dataTotalsFilter);

  const {
    token: {borderRadiusLG},
  } = theme.useToken();

  const {
    data: brief,
  } = useSWR<HydraData>(briefId > 0 && briefId !== 0 ? `${urls.briefs}/${briefId}?t[]=brief&t[]=brief:engine&t[]=engine` : null, api.get, {
    revalidateOnFocus: false
  });

  const getPeriodParams = () => {
    const params = new URLSearchParams([
      ['t[]', 'period'], /*['t[]', 'period:data'], ['t[]', 'data'], ['t[]', 'data:metrics'], ['t[]', 'data:channels'],*/
      ['t[]', 'dataChannel'], ['t[]', 'dataChannel:channel'], ['t[]', 'data:file'], ['t[]', 'period:plan'],
      ['t[]', 'media_object:read'], ['t[]', 'data:factors'], ['t[]', 'dataFactor:factor'], ['t[]', 'factor'],
    ]);

    return params.toString()
  };

  const {
    data: period,
  } = useSWR<{
    data: Period
  }>(periodId > 0 ? `${urls.periods}/${periodId}?${getPeriodParams()}` : null, api.get, {
    revalidateOnFocus: false
  });

  const isPeriodAccepted = () => {
    return dataTotals && dataTotals.some((dataTotal: DataTotal) => dataTotal.isAccepted)
  };

  const transferToBudgeting = () => {
    if (!Roles.hasAccess('canAcceptPlan')) {
      return
    }

    setIsTransferingToBudget(true);

    acceptPlan(selectedPlanId).then(() => {
      message.success(t('planner.successTransferredToBudgeting'));
      mutateDataTotals();
    }).catch((response) => {
      message.error(response['hydra:description'])
    }).finally(() => {
      setIsTransferingToBudget(false);
    });
  };

  const cancelTransferringToBudgeting = () => {
    if (!Roles.hasAccess('canAcceptPlan')) {
      return
    }

    setIsTransferingToBudget(true);

    new Promise((resolve) => {
      cancelPlanAccept(selectedPlanId).then(() => {
        message.success(t('planner.successCanceledTransferringToBudgeting'));
        setSelectedPlanId(0);
        mutateDataTotals();
      }).catch((response) => {
        message.error(response['hydra:description'])
      }).finally(() => {
        setIsTransferingToBudget(false);
      });
    })
  };

  useEffect(() => {
    if (dataTotals) {
      for (let dataTotal of dataTotals) {
        if (dataTotal.isAccepted) {
          setSelectedPlanId(dataTotal.id);
          break;
        }
      }

      if (!isSubscribedToDataTotals) {
        for (let dataTotal of dataTotals) {
          if (dataTotal.calculationStatus === CalculationStatus.PENDING || dataTotal.calculationStatus === CalculationStatus.RUNNING) {
            subscribeTo(`/api/engine/data_totals/${dataTotal.id}`, (successMessage) => {
              if (successMessage) {
                message.success(successMessage)
              }
            }, (errorMessage: string) => {
              message.error(errorMessage)
            }, (result) => {
              mutateDataTotal(dataTotal.id)
            })
          }
        }

        setIsSubscribedToDataTotals(true)
      }
    }
  }, [dataTotals]);

  const getPeriodTitle = () => {
    switch (period?.data?.periodLength) {
      case 0:
        return `Q${period.data.quarter}`;
      case 1:
        return '';
      case 2:
        return `H${period.data.quarter < 3 ? 1 : 2}`;
      default:
        return '';
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Layout: {
            headerBg: '#fff',
            siderBg: '#fff',
            fontFamily: 'Open Sans',
            fontFamilyCode: 'Open Sans',
          },
        },
      }}
    >
      <Layout>
        <Header style={{display: 'flex', alignItems: 'center', justifyContent: 'start'}}>
          {period && period.data &&
            <Row justify={"space-between"} style={{flex: 1}} align={"middle"}>
              <Col span={4}>
                <Link to={{pathname: '/briefs/actual'}}>
                  <Flex gap={'small'} align={'center'}>
                    <IconBackArrow/>
                  </Flex>
                </Link>
              </Col>
              <Col span={16}>
                <Flex>
                  <strong>{t('planner.title')}:&nbsp;</strong>
                  <Tooltip placement="bottom" title={t('planner.returnToModel')}>
                    <Link to={{pathname: `/briefs/${briefId}/description`}} className={'planner__header-title'}>
                      {brief && brief.data && brief.data.name || ''} —
                      <strong>
                        {getPeriodTitle()} (
                        {dayjs(period.data.dateStart).format(dateFormat)} — {dayjs(period.data.dateEnd).format(dateFormat)})</strong>
                    </Link>
                  </Tooltip>
                </Flex>
              </Col>
              <Col span={4}>
                <Flex gap={'small'} align={'center'} justify={'flex-end'}>
                  <Flex justify={'space-between'} align={'center'} gap={'large'}>
                    {isPeriodAccepted()
                      ?
                      <Popconfirm
                        placement="bottom"
                        title={t('planner.cancelTransferringToBudgeting')}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                        onConfirm={cancelTransferringToBudgeting}
                      >
                        <ButtonBudgeting
                          loading={isTransferingToBudget || isDataTotalsLoading}
                        >{t('planner.transferredToBudgeting')}</ButtonBudgeting>
                      </Popconfirm>
                      :
                      <ButtonBlack
                        onClick={() => transferToBudgeting()}
                        disabled={!selectedPlanId}
                        loading={isTransferingToBudget || isDataTotalsLoading}
                      >
                        {t('planner.transferToBudgeting')}
                      </ButtonBlack>
                    }
                  </Flex>
                </Flex>
              </Col>
            </Row>
          }
        </Header>
        <Layout>
          <Sider width={48}></Sider>
          <Layout>
            <Content
              style={{
                minHeight: 'calc(100vh - 96px)',
                background: '#F1F5F7',
                borderRadius: borderRadiusLG,
              }}
            >
              <Flex gap={'small'} justify={'flex-end'} style={{padding: '10px', width: '100%'}}>
                {(!period || !period.data) ?
                  <Card style={{width: '100%'}}>
                    Период не найден
                  </Card>
                  :
                  <Flex vertical gap={'small'} style={{width: '100%'}}>
                    {showCompare &&
                      <Card>
                        <CompareItems
                          dataTotals={dataTotals}
                          selectedForComparesIds={selectedForComparesIds}
                          deleteFromCompares={(compareId: string) => {
                            setSelectedForComparesIds([...selectedForComparesIds.filter(function (compareItemId, index, arr) {
                              return compareItemId !== compareId
                            })])
                          }}
                          close={() => setShowCompare(false)}
                          historicDataTotal={historicData}
                        />
                      </Card>
                    }
                    <Flex gap={'small'} style={{width: '100%'}}>
                      <Card style={{width: '400px'}}>
                        <Flex vertical gap={20}>
                          <Summary
                            brief={brief ? brief.data : null}
                            dataTotals={dataTotals}
                            selectedPlanId={selectedPlanId}
                            historicData={historicData}
                            showFactTotal={showHistoricData}
                          />
                        </Flex>
                      </Card>
                      <Card style={{width: '100%', flex: 1}}>
                        <DataTotals
                          period={period.data}
                          dataTotals={dataTotals}
                          mutateDataTotals={mutateDataTotals}
                          setDataTotalsFilter={setDataTotalsFilter}
                          isDataTotalsLoading={isDataTotalsLoading}
                          isDataTotalLoading={isDataTotalLoading}
                          selectedPlanId={selectedPlanId}
                          setSelectedPlanId={setSelectedPlanId}
                          brief={brief ? brief.data : null}
                          selectedForComparesIds={selectedForComparesIds}
                          setSelectedForCompares={setSelectedForComparesIds}
                          showCompare={showCompare}
                          setShowCompare={setShowCompare}
                          setShowFactTotal={setShowFactTotal}
                          mutateDataTotal={mutateDataTotal}
                          showFactData={showHistoricData}
                          setShowFactData={setShowHistoricData}
                          isFactData={isFactData}
                          dataTotalsFilter={dataTotalsFilter}
                          setHistoricData={setHistoricData}
                        />
                      </Card>
                    </Flex>
                  </Flex>
                }
              </Flex>
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  )
}
